import {
  HbrOption,
  HbrSelect,
  HbrTextarea,
  HbrBanner,
  HbrIcon,
} from '@harbor/elements-react-standalone';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  useCreateApprovalHistoryMutation,
  useCreateEscalationApprovalMutation,
  useUpdateApprovalRequestMutation,
} from '../../../hooks/approval-request-hooks';
import {
  useCreateEscalationWorkflowMutation,
  useUpdateEscalationMutation,
} from '../../../hooks/escalation-request-hooks';
import { useFetchOpportunity } from '../../../hooks/opportunity-hooks';
import { RootState, useAppDispatch } from '../../../support/store';
import {
  ApprovalRequest,
  ApprovalRequestPayload,
  ApprovalRequestSource,
  ApprovalRequestStatusLevel,
  ApprovalRequestType,
  EscalationLevel,
  EscalationRequest,
  RequestHistoryPayload,
} from '../../../types/approval-request';
import { Product } from '../../../types/product';
import ApprovalRequestModal from '../../Approvals/ApprovalRequestModal';
import { tagOpportunity } from '../../../support/slices/salesforceOpportunitiesSlice';
import StbButton from '../../security-toolbox-ui/StbButton';
import { alertsInfoLarge } from '@harbor/elements/icons';
import StbLoader from '../../security-toolbox-ui/StbLoader';
import { tagUserToOpportunity } from '../../../support/opportunityHelpers';

const OpportunityEscalationModal: React.FC<{
  open: boolean;
  setOpen?: (open: boolean) => void;
  availableProducts?: Product[];
  request?: ApprovalRequest;
  requestProduct?: Product;
  requestEscalation?: EscalationRequest;
  isEscalating?: boolean;

  /**
   * Dynamic target support:
   * - Level 1 can escalate to 2 or directly to 3 if 2 is unavailable.
   * - Level 2 can escalate to 3.
   */
  targetEscalationLevel?: EscalationLevel;
  targetAssignmentTypeId?: number;
}> = ({
  open,
  setOpen: setRequestModalOpen,
  availableProducts = [],
  request,
  requestProduct,
  requestEscalation,
  isEscalating = false,
  targetEscalationLevel = EscalationLevel.Incubation,
  targetAssignmentTypeId = EscalationLevel.Incubation,
}) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);
  const dispatch = useAppDispatch();

  const requestItemType = ApprovalRequestType.Escalation;
  const requestObjectIds = {
    [ApprovalRequestSource.Opportunity]: opportunityId,
  };

  const { mutateAsync: createApprovalHistory } = useCreateApprovalHistoryMutation();
  const { mutateAsync: createEscalationWorkflow } = useCreateEscalationWorkflowMutation(opportunityId);
  const { mutateAsync: updateApprovalRequest } = useUpdateApprovalRequestMutation(requestObjectIds);
  const { mutateAsync: updateEscalation } = useUpdateEscalationMutation(opportunityId);
  const { mutateAsync: createEscalationApproval } = useCreateEscalationApprovalMutation(opportunityId);

  const { id: requesterId } = useSelector((state: RootState) => state.user.userData);
  const { id: approverId } = useSelector((state: RootState) => state.user.manager);

  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [justification, setJustification] = useState('');
  const [justificationTouched, setJustificationTouched] = useState(false);
  const [taggingRequested, setTaggingRequested] = useState(false);

  const taggedOpportunityIds = useSelector((state: RootState) => state.salesforceOpportunities.taggedOpportunityIds);
  const isTagged = useMemo(() => !!opportunityId && taggedOpportunityIds.includes(opportunityId), [taggedOpportunityIds, opportunityId]);

  useEffect(() => {
    if (!open || !request) return;
    setJustification(isEscalating ? '' : request?.justification ?? '');
  }, [request, open, isEscalating]);

  useEffect(() => {
    if (!open || !requestProduct) return;
    setSelectedProduct(String(requestProduct.id));
  }, [requestProduct, open]);

  const product = useMemo(() => {
    if (requestProduct) return requestProduct;
    if (!availableProducts || !selectedProduct) return null;
    return availableProducts?.find((p) => String(p.id) === selectedProduct) ?? null;
  }, [availableProducts, selectedProduct, requestProduct]);

  const requestTitle =
    request?.title ??
    `Opportunity Escalation ${!product ? '' : `- ${product.name}`} - ${
      opportunity?.Name ?? 'Unknown Opportunity'
    }`;

  const submitDisabled = justification.trim() === '' || !selectedProduct;

  const productOptions = useMemo(() => {
    if (requestProduct) {
      return [{ value: String(requestProduct.id), label: requestProduct.name }];
    }

    if (!availableProducts) return [];

    return availableProducts.map((p) => ({
      value: String(p.id),
      label: p.name,
    }));
  }, [availableProducts, requestProduct]);

  const handleRequest = async () => {
    const status = ApprovalRequestStatusLevel.Pending;

    if (!request) {
      const result = await createEscalationWorkflow({
        create_escalation: {
          opportunity_id: opportunityId,
          product_id: Number(selectedProduct),
          escalation_type_id: 1,
          requested_by_id: requesterId,
          status_id: status,
          description: justification,
        },
        create_approval_request: {
          title: requestTitle,
          requested_item_type: requestItemType,
          justification,
          requestor_id: requesterId,
          approver_id: approverId,
          status_id: status,
        },
        create_escalation_approval: {
          level: EscalationLevel.Domain,
          target_assignment_type_id: EscalationLevel.Domain,
        },
      });

      if (result?.created?.approval_request) {
        await createApprovalHistory({
          approval_request_id: result.approval_request.id,
          previous_status_id: null,
          current_status_id: status,
          previous_justification: null,
          current_justification: justification ?? null,
          action_by_user_id: requesterId,
        });
      }
    } else if (isEscalating && request && requestEscalation) {
      const payload = {
        escalation_id: requestEscalation.id,
        target_assignment_type_id: Number(targetAssignmentTypeId),
        level: Number(targetEscalationLevel),
        create_approval_request: {
          title: `Re-escalation - ${requestTitle}`,
          requested_item_type: ApprovalRequestType.Escalation,
          justification,
          status_id: status,
          requestor_id: requesterId,
          approver_id: approverId,
        },
      };

      const result = await createEscalationApproval(payload);

      if (result?.approval_request) {
        await createApprovalHistory({
          approval_request_id: result.approval_request.id,
          previous_status_id: null,
          current_status_id: status,
          previous_justification: null,
          current_justification: justification ?? null,
          action_by_user_id: requesterId,
        });
      }
    } else {
      const payload: Partial<ApprovalRequestPayload> = {
        id: request.id,
        title: request?.title,
        requestor_id: request?.requestor.id,
        approver_id: request?.approver.id,
        requested_item_type: request?.requested_item_type,
        justification,
        status_id: status,
      };

      const result = await updateApprovalRequest(payload as ApprovalRequest);

      if (result?.id) {
        const escalationPayload = {
          id: requestEscalation.id,
          description: justification,
        };

        await updateEscalation(escalationPayload);

        const history: RequestHistoryPayload = {
          previous_status_id: request?.status?.id ?? null,
          current_status_id: status,
          previous_justification: request?.justification ?? null,
          current_justification: justification ?? null,
          action_by_user_id: request?.requestor.id ?? null,
          approval_request_id: request.id,
        };

        await createApprovalHistory(history);
      }
    }
  };

  const tagUser = async () => {
    setTaggingRequested(true);
    await tagUserToOpportunity(opportunity);
    dispatch(tagOpportunity(opportunity.Id));
  }

  const handleClose = async () => {
    setRequestModalOpen(false);
    setJustification('');
    setJustificationTouched(false);
    setSelectedProduct(null);
  };

  return (
    <ApprovalRequestModal
      open={open}
      requestTitle={`${isEscalating ? 'Re-escalation - ' : ''}${requestTitle}`}
      requestObjectIds={requestObjectIds}
      handleClose={handleClose}
      handleRequest={handleRequest}
      submitDisabled={submitDisabled}
      requestItemType={requestItemType}
    >
      <>
        {isTagged && (
          <>
            <HbrSelect
              value={String(selectedProduct ?? '')}
              onHbr-input={(e) => setSelectedProduct(e.target.value as string)}
              disabled={!!request}
            >
              {(productOptions ?? []).map((product) => (
                <HbrOption key={product.value} value={product.value}>
                  {product.label}
                </HbrOption>
              ))}
              <div slot="label">
                Product Solution <em className="text-gray-500">(required)</em>
              </div>
            </HbrSelect>

            <HbrTextarea
              value={justification}
              onHbr-input={(evt) => {
                const value = evt.target.value;
                setJustificationTouched(true);
                setJustification(value);
              }}
              placeholder="Add extra details to support your request..."
              rows={3}
              error={justificationTouched && justification?.trim() === ''}
            >
              <div slot="label">
                Escalation Details <em className="text-gray-500">(required)</em>
              </div>

              {justificationTouched && justification?.trim() === '' && (
                <div slot="help-text" className="text-sm mt-2 text-danger">
                  Details are required to support your request
                </div>
              )}
            </HbrTextarea>
          </>
        )}
        {!isTagged && (
          <>
            <HbrBanner
              variant="fill"
              open
              sentiment="info"
              className="mb-4"
            >
              <div className="flex items-center gap-2">
                <HbrIcon asset={alertsInfoLarge} sentiment="info" size="24px" />
                You must be tagged to this opportunity enable escalation requests.
              </div>
            </HbrBanner>
            <StbLoader isLoading={taggingRequested} />
            <StbButton onClick={tagUser} disabled={taggingRequested}>Tag yourself now</StbButton>
          </>
        )}
      </>
    </ApprovalRequestModal>
  );
};

export default OpportunityEscalationModal;